import { useEffect, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useRoute } from 'wouter';
import { api, ApiError } from '../lib/api';
import { useUpdateBrief, type Project } from '../lib/projects';
import { useRefreshTeam, useResetTeam, useRunSetup, useSetupCounts, useSetupOutputs } from '../lib/setup';
import { useLivePipeline, useRuns } from '../lib/pipeline';
import { ProjectHeader } from '../components/ProjectHeader';
import { ChooseWizard, type StepKey } from '../components/ChooseWizard';
import { VoiceIntakeModal } from '../components/VoiceIntakeModal';
import { NextStep } from '../components/NextStep';

const ALL_CHANNELS = ['linkedin', 'instagram'] as const;
type Channel = (typeof ALL_CHANNELS)[number];
type SetupTab = 'source' | 'team' | 'samples';

const SETUP_NODES = ['parser', 'audience', 'voice', 'persona', 'theme', 'brandkit', 'sample'] as const;
const SETUP_LABELS: Record<string, string> = {
  parser: 'Reading source',
  audience: 'Audience options',
  voice: 'Voice options',
  persona: 'Persona options',
  theme: 'Theme options',
  brandkit: 'Visual style',
  sample: 'Sample posts',
};

export function BrandPage() {
  const [, paramsSetup] = useRoute('/projects/:id/setup');
  const [, paramsLegacy] = useRoute('/projects/:id/brand');
  const params = paramsSetup ?? paramsLegacy;
  const id = params?.id;
  const query = useQuery({
    enabled: !!id,
    queryKey: ['projects', id],
    queryFn: () => api<{ project: Project }>(`/api/projects/${id}`).then((r) => r.project),
  });
  const project = query.data;

  if (!id) return null;

  return (
    <div className="min-h-full">
      <ProjectHeader projectId={id} page="SETUP" />
      <main className="max-w-5xl mx-auto px-4 md:px-6 py-4 md:py-8 space-y-8">
        {query.isLoading && (
          <p className="font-mono text-xs uppercase tracking-wider text-muted">Loading brand…</p>
        )}
        {project && <SetupWorkspace project={project} />}
      </main>
    </div>
  );
}


function SetupWorkspace({ project }: { project: Project }) {
  const [activeTab, setActiveTab] = useState<SetupTab>('source');

  return (
    <>
      <NextStep projectId={project.id} />
      <SetupHero project={project} />
      <SetupTabs active={activeTab} onChange={setActiveTab} />
      {activeTab === 'source' && <BriefForm project={project} />}
      {activeTab === 'team' && <BrandTeamSection project={project} />}
      {activeTab === 'samples' && <SampleWorkSection project={project} />}
    </>
  );
}

function SetupHero({ project }: { project: Project }) {
  return (
    <section className="rounded-[1.75rem] border border-border-color bg-surface p-4 md:p-6 overflow-hidden relative">
      <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-accent-cyan/10 to-transparent pointer-events-none" />
      <div className="relative max-w-3xl">
        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-accent-cyan mb-3">Strategy</p>
        <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight">Assemble the strategy team for {project.name}.</h1>
        <p className="mt-3 text-sm text-muted leading-relaxed">
          Share the client pack, brief each specialist, then judge sample work before the content team takes over the day-to-day.
        </p>
      </div>
    </section>
  );
}

function SetupTabs({ active, onChange }: { active: SetupTab; onChange: (tab: SetupTab) => void }) {
  const tabs: Array<{ key: SetupTab; n: string; label: string; helper: string; icon: string }> = [
    { key: 'source', n: '01', label: 'Source material', helper: 'Client pack', icon: 'folder_open' },
    { key: 'team', n: '02', label: 'Strategy team', helper: 'Specialist rooms', icon: 'groups' },
    { key: 'samples', n: '03', label: 'Sample work', helper: 'Proof before campaigns', icon: 'rate_review' },
  ];

  return (
    <div className="sticky top-[57px] z-[9] rounded-[1.25rem] border border-border-color bg-background-dark/95 p-2 shadow-xl shadow-black/20 backdrop-blur">
      <div role="tablist" aria-label="Strategy rooms" className="grid md:grid-cols-3 gap-1">
        {tabs.map((tab) => {
          const selected = active === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onChange(tab.key)}
              className={`relative flex items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors ${
                selected
                  ? 'bg-surface text-text-primary shadow-lg shadow-black/20'
                  : 'text-muted hover:bg-surface/60 hover:text-text-primary'
              }`}
            >
              <span className={`font-mono text-[11px] font-bold tracking-wider ${selected ? 'text-accent-cyan' : 'text-muted/80'}`}>{tab.n}</span>
              <span className={`material-symbols-outlined text-[21px] ${selected ? 'text-accent-cyan' : 'text-muted'}`}>{tab.icon}</span>
              <span className="min-w-0">
                <span className="block font-display text-lg font-bold leading-tight">{tab.label}</span>
                <span className="block text-xs text-muted mt-0.5">{tab.helper}</span>
              </span>
              {selected && <span className="absolute inset-x-4 bottom-0 h-0.5 rounded-full bg-accent-cyan" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ───────── Brief form ─────────

function BriefForm({ project }: { project: Project }) {
  const update = useUpdateBrief(project.id);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [brief, setBrief] = useState(project.brief ?? '');
  const [logoUrl, setLogoUrl] = useState(project.logoUrl ?? '');
  const [logoIncludesWordmark, setLogoIncludesWordmark] = useState(project.logoIncludesWordmark ?? true);
  const [publicUrl, setPublicUrl] = useState(project.publicUrl ?? '');
  const [channels, setChannels] = useState<Channel[]>(
    (project.channels.filter((c) => ALL_CHANNELS.includes(c as Channel)) as Channel[]) ?? ['linkedin', 'instagram'],
  );
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setBrief(project.brief ?? '');
    setLogoUrl(project.logoUrl ?? '');
    setLogoIncludesWordmark(project.logoIncludesWordmark ?? true);
    setPublicUrl(project.publicUrl ?? '');
    setChannels(project.channels.filter((c) => ALL_CHANNELS.includes(c as Channel)) as Channel[]);
  }, [project.id, project.brief, project.logoUrl, project.logoIncludesWordmark, project.publicUrl, project.channels]);

  const toggleChannel = (ch: Channel) =>
    setChannels((prev) => (prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch]));

  async function onSave(e: FormEvent) {
    e.preventDefault();
    setSaved(false);
    await update.mutateAsync({
      brief: brief.trim() === '' ? null : brief,
      logoUrl: logoUrl.trim() === '' ? null : logoUrl.trim(),
      logoIncludesWordmark,
      publicUrl: publicUrl.trim() === '' ? null : publicUrl.trim(),
      channels,
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  const dirty =
    (project.brief ?? '') !== brief ||
    (project.logoUrl ?? '') !== logoUrl.trim() ||
    (project.logoIncludesWordmark ?? true) !== logoIncludesWordmark ||
    (project.publicUrl ?? '') !== publicUrl.trim() ||
    project.channels.slice().sort().join(',') !== channels.slice().sort().join(',');

  const errorMessage = update.error instanceof ApiError ? update.error.message : null;

  return (
    <section className="space-y-4">
      <header>
        <h2 className="font-display text-2xl font-bold tracking-tight">Source material</h2>
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted mt-1">
          Give the team the raw material. You can update this later as the brand evolves.
        </p>
      </header>

      <VoiceIntakeModal open={intakeOpen} onClose={() => setIntakeOpen(false)} />

      <form onSubmit={onSave} className="space-y-5">
        <div className="rounded border border-border-color/60 bg-surface-2/40 px-4 py-3 flex items-start gap-4">
          <div className="flex-1">
            <p className="font-mono text-[10px] uppercase tracking-wider text-accent-cyan mb-1">
              Step 1 · Run a voice intake
            </p>
            <p className="text-sm text-text-primary leading-relaxed">
              The fastest way to write a brief your team can publish on-voice from is a 30–45 minute strategic interview. Open the prompt and either run it yourself in Claude / ChatGPT / Gemini voice mode, or send it to the brand's founder for them to run. Either way, paste the markdown they produce into the brief below.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIntakeOpen(true)}
            className="shrink-0 self-center font-mono text-[11px] uppercase tracking-wider px-3 py-2 rounded bg-accent-cyan/15 text-accent-cyan border border-accent-cyan/40 hover:bg-accent-cyan/25 transition-colors flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-[16px]">mic</span>
            <span>Open prompt</span>
          </button>
        </div>

        <div>
          <label className="font-mono text-[10px] uppercase tracking-wider text-muted block mb-1.5">
            Brand brief
          </label>
          <textarea
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            rows={14}
            placeholder={'Paste the output of your voice intake here, or write the brief manually.\n\nCover: product, audience, villain, voice, what the voice refuses to do, visual, content shape, words to use and avoid, reference posts, anti-conventions (competitor examples you would NOT publish), and inconvenient truths (what the category avoids saying).'}
            className="w-full bg-surface-2 border border-border-color rounded px-3 py-2 text-sm font-mono leading-relaxed text-text-primary placeholder-muted focus:outline-none focus:border-accent-cyan transition-colors"
          />
          <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-muted">
            {brief.length.toLocaleString()} chars
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-wider text-muted block mb-1.5" htmlFor="logoUrl">
              Logo URL
            </label>
            <input
              id="logoUrl"
              type="url"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              placeholder="https://example.com/logo.png"
              className="w-full bg-surface-2 border border-border-color rounded px-3 py-2 text-sm font-mono text-text-primary placeholder-muted focus:outline-none focus:border-accent-cyan transition-colors"
            />
            {logoUrl && (
              <div className="mt-2 inline-flex items-center gap-2 rounded border border-border-color p-2">
                <img
                  src={logoUrl}
                  alt="Logo preview"
                  className="h-10 w-10 object-contain"
                  onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')}
                />
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted">preview</span>
              </div>
            )}
            <label className="mt-3 flex items-start gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={logoIncludesWordmark}
                onChange={(e) => setLogoIncludesWordmark(e.target.checked)}
                className="mt-0.5 accent-accent-cyan"
              />
              <span className="text-xs text-muted leading-relaxed">
                Logo already includes the brand name. When ticked, slides and reels show the logo without a separate wordmark beside it. Untick if your logo is icon-only.
              </span>
            </label>
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-wider text-muted block mb-1.5" htmlFor="publicUrl">
              Public URL
            </label>
            <input
              id="publicUrl"
              type="url"
              value={publicUrl}
              onChange={(e) => setPublicUrl(e.target.value)}
              placeholder="https://your-brand.example"
              className="w-full bg-surface-2 border border-border-color rounded px-3 py-2 text-sm font-mono text-text-primary placeholder-muted focus:outline-none focus:border-accent-cyan transition-colors"
            />
            <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-muted">
              Renders at the bottom of CTA slides.
            </p>
          </div>
        </div>

        <div>
          <div className="flex items-baseline justify-between gap-3 mb-2">
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted">Channels</p>
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted">Choose one or both</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {ALL_CHANNELS.map((ch) => {
              const active = channels.includes(ch);
              const label = ch === 'linkedin' ? 'LinkedIn' : 'Instagram';
              const helper = ch === 'linkedin' ? 'PDF carousel + professional post body' : 'PNG carousel set + caption';
              return (
                <label
                  key={ch}
                  className={`relative flex cursor-pointer gap-3 rounded-xl border p-4 transition-colors ${
                    active
                      ? 'bg-primary/10 border-primary text-text-primary'
                      : 'bg-surface-2/40 border-border-color text-muted hover:border-text-primary hover:text-text-primary'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={() => toggleChannel(ch)}
                    className="mt-1 h-4 w-4 accent-primary"
                  />
                  <span>
                    <span className="block font-display text-lg font-bold">{label}</span>
                    <span className="mt-1 block text-xs leading-relaxed text-muted">{helper}</span>
                  </span>
                  {active && (
                    <span className="material-symbols-outlined absolute right-3 top-3 text-accent-emerald text-[18px]">
                      check_circle
                    </span>
                  )}
                </label>
              );
            })}
          </div>
          {channels.length === 0 && (
            <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-accent-red">
              Pick at least one channel.
            </p>
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-border-color pt-4">
          <button
            type="submit"
            disabled={!dirty || update.isPending || channels.length === 0}
            className="btn-bracket bg-primary text-white px-4 py-2 hover:bg-primary/90 disabled:opacity-40 transition-colors"
          >
            {update.isPending ? 'Saving…' : 'Save brand brief'}
          </button>
          {saved && <span className="font-mono text-[10px] uppercase tracking-wider text-accent-emerald">Saved</span>}
          {errorMessage && (
            <span className="font-mono text-[10px] uppercase tracking-wider text-accent-red">Could not save: {errorMessage}</span>
          )}
          {!dirty && !saved && !errorMessage && project.brief && (
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted">Saved</span>
          )}
        </div>
      </form>
    </section>
  );
}

// ───────── Candidates section + wizard ─────────

function BrandTeamSection({ project }: { project: Project }) {
  const runs = useRuns(project.id);
  const latestSetupRun = runs.data?.runs.find((r) => r.pipeline === 'setup');
  const isRunning = latestSetupRun?.status === 'running';
  const latestSetupError = latestSetupRun?.pipeline === 'setup' && latestSetupRun.status === 'failed' ? latestSetupRun.error : null;

  const setup = useSetupOutputs(project.id, isRunning);
  const run = useRunSetup(project.id);
  const refresh = useRefreshTeam(project.id);
  const reset = useResetTeam(project.id);
  const counts = useSetupCounts(project.id);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [refreshConfirmOpen, setRefreshConfirmOpen] = useState(false);
  const { nodeStatus, lastEvent } = useLivePipeline(project.id);

  const [reviewStep, setReviewStep] = useState<StepKey | null>(null);

  const briefReady = !!project.brief && project.brief.trim().length >= 30;
  const errorMessage = run.error instanceof ApiError ? run.error.message : null;
  const data = setup.data;
  const hasOutputs = !!data && (data.personas.length > 0 || data.audiences.length > 0 || !!data.brandKit);
  const showProgress = isRunning || run.isPending;

  const completed = SETUP_NODES.filter((n) => nodeStatus[n] === 'done').length;
  const currentNode = SETUP_NODES.find((n) => nodeStatus[n] === 'running');

  const activeAudiences = data?.audiences.filter((a) => a.isSelected) ?? [];
  const activeVoices = data?.voices.filter((v) => v.isSelected) ?? [];
  const activePersonas = data?.personas.filter((p) => p.isActive) ?? [];
  const activeThemes = data?.themes.filter((t) => t.isActive) ?? [];
  const brandKitLabel = data?.brandKit ? `${data.brandKit.fonts.heading} / ${data.brandKit.fonts.body}` : null;

  const team = [
    {
      key: 'audience' as StepKey,
      role: 'Audience strategist',
      icon: 'groups',
      brief: 'Who are we really speaking to?',
      status: memberStatus(showProgress, activeAudiences.length > 0 && hasOutputs, data?.audiences.length ?? 0),
      metric: data?.audiences.length ? `${activeAudiences.length}/${data.audiences.length} active` : 'No audiences yet',
      detail: activeAudiences.slice(0, 2).map((a) => a.name).join(', ') || 'Switch on the audience briefs this brand should speak to.',
    },
    {
      key: 'voice' as StepKey,
      role: 'Voice strategist',
      icon: 'edit_note',
      brief: 'How should the brand sound?',
      status: memberStatus(showProgress, activeVoices.length > 0 && hasOutputs, data?.voices.length ?? 0),
      metric: data?.voices.length ? `${activeVoices.length}/${data.voices.length} active` : 'No voices yet',
      detail: activeVoices.slice(0, 2).map((v) => v.name).join(', ') || 'Switch on the writing briefs this brand can use.',
    },
    {
      key: 'persona' as StepKey,
      role: 'Content strategists',
      icon: 'psychology',
      brief: 'Which recurring angles should we keep?',
      status: memberStatus(showProgress, activePersonas.length > 0 && hasOutputs, data?.personas.length ?? 0),
      metric: data?.personas.length ? `${activePersonas.length}/${data.personas.length} active` : 'No personas yet',
      detail: activePersonas.slice(0, 3).map((p) => p.name).join(', ') || 'Keep the personas that should repeatedly create posts.',
    },
    {
      key: 'theme' as StepKey,
      role: 'Campaign planner',
      icon: 'route',
      brief: 'What topic territories should campaigns rotate through?',
      status: memberStatus(showProgress, activeThemes.length > 0 && hasOutputs, data?.themes.length ?? 0),
      metric: data?.themes.length ? `${activeThemes.length}/${data.themes.length} active` : 'No themes yet',
      detail: 'Themes become the campaign lanes your content team rotates through.',
    },
    {
      key: 'brandkit' as StepKey,
      role: 'Visual designer',
      icon: 'palette',
      brief: 'What should the posts look like?',
      status: memberStatus(showProgress, !!brandKitLabel, data?.brandKit ? 1 : 0),
      metric: brandKitLabel ?? 'No visual style yet',
      detail: 'Palette and fonts feed the carousel renderer.',
    },
  ];

  const readyCount = team.filter((m) => m.status === 'ready').length;
  const needsReviewCount = team.filter((m) => m.status === 'needs-review').length;
  const allReady = readyCount === team.length;

  function openRoom(step: StepKey) {
    if (!hasOutputs || showProgress) return;
    setReviewStep(step);
  }

  return (
    <section className="space-y-4">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight">Strategy team</h2>
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted mt-1">
            Prepare the specialists, then brief each one until the strategy team is ready to hand off to the content team.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {hasOutputs ? (
            <>
              <button
                type="button"
                onClick={() => setRefreshConfirmOpen(true)}
                disabled={!briefReady || refresh.isPending || reset.isPending || isRunning}
                className="btn-bracket bg-primary text-white px-3 py-1.5 hover:bg-primary/90 disabled:opacity-40 transition-colors"
                title="Update the team to reflect changes in the brief. Existing names stay; only revised content."
              >
                {showProgress || refresh.isPending ? 'Refreshing team…' : 'Refresh team'}
              </button>
              <button
                type="button"
                onClick={() => setResetConfirmOpen(true)}
                disabled={!briefReady || refresh.isPending || reset.isPending || isRunning}
                className="btn-bracket border border-accent-red/50 text-accent-red px-3 py-1.5 hover:bg-accent-red/10 disabled:opacity-40 transition-colors"
                title="Wipe the team and start over. Use when the brief has changed materially."
              >
                Reset team
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => run.mutate()}
              disabled={!briefReady || run.isPending || isRunning}
              className="btn-bracket bg-primary text-white px-3 py-1.5 hover:bg-primary/90 disabled:opacity-40 transition-colors"
              title={!briefReady ? 'Save source material first' : undefined}
            >
              {showProgress ? 'Preparing team…' : 'Prepare team'}
            </button>
          )}
        </div>
      </header>

      {resetConfirmOpen && (
        <ResetConfirmDialog
          counts={counts.data?.counts}
          isResetting={reset.isPending}
          onCancel={() => setResetConfirmOpen(false)}
          onConfirm={async () => {
            try {
              await reset.mutateAsync();
              setResetConfirmOpen(false);
            } catch {
              /* error surfaces via mutation state below */
            }
          }}
        />
      )}

      {refreshConfirmOpen && (
        <RefreshConfirmDialog
          counts={counts.data?.counts}
          change={counts.data?.change}
          isRefreshing={refresh.isPending}
          onCancel={() => setRefreshConfirmOpen(false)}
          onConfirm={async () => {
            try {
              await refresh.mutateAsync();
              setRefreshConfirmOpen(false);
            } catch {
              /* error surfaces via mutation state below */
            }
          }}
          onReset={() => {
            setRefreshConfirmOpen(false);
            setResetConfirmOpen(true);
          }}
        />
      )}

      {(errorMessage || latestSetupError) && (
        <div className="rounded-lg border border-accent-red/40 bg-accent-red/10 p-3">
          <p className="font-mono text-[10px] uppercase tracking-wider text-accent-red">
            Your strategy team hit a snag: {errorMessage ?? latestSetupError}
          </p>
          {latestSetupError?.includes('Incorrect API key') && (
            <p className="mt-2 text-xs text-muted">The local backend has an invalid OpenAI key. Update OPENAI_API_KEY in .env and restart the backend.</p>
          )}
        </div>
      )}

      {!briefReady && (
        <p className="font-mono text-[10px] uppercase tracking-wider text-accent-amber">
          Save the source material above before preparing the team.
        </p>
      )}

      <div className="grid md:grid-cols-[1fr_260px] gap-4 items-stretch">
        <div className="grid sm:grid-cols-2 gap-3">
          {team.map((member) => (
            <TeamMemberCard
              key={member.key}
              member={member}
              disabled={!hasOutputs || showProgress}
              onOpen={() => openRoom(member.key)}
            />
          ))}
        </div>

        <aside className="rounded-[1.25rem] border border-border-color bg-surface p-4 flex flex-col justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted">Team readiness</p>
            <p className="mt-2 font-display text-2xl md:text-4xl font-bold tracking-tight">{readyCount}/{team.length}</p>
            <p className="mt-2 text-sm text-muted leading-relaxed">
              {showProgress
                ? 'The team is working through the source material.'
                : allReady
                  ? 'Your strategy team is ready. You can brief the content team for the first post.'
                  : hasOutputs
                    ? `${needsReviewCount || team.length - readyCount} specialist${(needsReviewCount || team.length - readyCount) === 1 ? '' : 's'} still need your feedback.`
                    : 'Prepare the team to create the first set of specialist briefs.'}
            </p>
          </div>

          {showProgress && (
            <div className="rounded-lg border border-accent-cyan/40 bg-accent-cyan/10 p-3">
              <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-wider">
                <span className="text-accent-cyan">{currentNode ? SETUP_LABELS[currentNode] : 'Starting'} is working</span>
                <span className="text-text-primary">{completed} / {SETUP_NODES.length}</span>
              </div>
              <div className="mt-2 h-1 w-full bg-accent-cyan/10 rounded overflow-hidden">
                <div className="h-full bg-accent-cyan transition-all" style={{ width: `${(completed / SETUP_NODES.length) * 100}%` }} />
              </div>
              {lastEvent && <p className="mt-2 font-mono text-[10px] text-muted">latest: {lastEvent.agentName} · {lastEvent.eventType}</p>}
            </div>
          )}

          {allReady && !showProgress && (
            <Link href={`/projects/${project.id}/generator`} className="btn-bracket rounded-xl bg-accent-cyan/20 border border-accent-cyan/40 px-3 py-2 text-center text-accent-cyan hover:bg-accent-cyan/30 transition-colors">
              Open content team
            </Link>
          )}
        </aside>
      </div>

      {reviewStep && <ChooseWizard projectId={project.id} initialStep={reviewStep} onClose={() => setReviewStep(null)} />}
    </section>
  );
}

function SampleWorkSection({ project }: { project: Project }) {
  const runs = useRuns(project.id);
  const latestSetupRun = runs.data?.runs.find((r) => r.pipeline === 'setup');
  const isRunning = latestSetupRun?.status === 'running';
  const setup = useSetupOutputs(project.id, isRunning);
  const samples = setup.data?.samples ?? [];
  const personas = setup.data?.personas ?? [];
  const samplesByPersona = new Map(personas.map((p) => [p.id, p.name]));

  return (
    <section className="space-y-4">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight">Sample work</h2>
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted mt-1">
            The proof of work. If this feels wrong, update the source material or brief a specific team member.
          </p>
        </div>
        {samples.length > 0 && <span className="status-pill status-pill-cyan">{samples.length} samples</span>}
      </header>

      {isRunning && (
        <div className="rounded-[1.25rem] border border-dashed border-accent-cyan/40 bg-accent-cyan/10 p-4 md:p-8 text-center">
          <p className="text-sm text-muted">The team is preparing sample work from the current source material.</p>
        </div>
      )}

      {!isRunning && samples.length === 0 && (
        <div className="rounded-[1.25rem] border border-dashed border-border-color bg-surface/60 p-4 md:p-8 text-center">
          <p className="text-sm text-muted">No sample work yet. Prepare your strategy team above, then use this section to decide whether they're ready.</p>
        </div>
      )}

      {samples.length > 0 && (
        <div className="grid md:grid-cols-2 gap-3">
          {samples.map((sample) => (
            <article key={sample.id} className="rounded-[1.25rem] border border-border-color bg-surface p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-muted">{sample.format.replace('_', ' ')}</p>
                  <h3 className="mt-1 font-display text-xl font-bold leading-tight">{sample.title}</h3>
                </div>
                <span className="status-pill status-pill-muted">sample</span>
              </div>
              <p className="mt-3 text-sm text-muted leading-relaxed whitespace-pre-wrap">{sample.body}</p>
              <p className="mt-4 text-[10px] uppercase tracking-wider text-muted">
                {samplesByPersona.get(sample.personaId) ?? 'Strategy team'}
              </p>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

type MemberStatus = 'not-ready' | 'working' | 'needs-review' | 'ready';

function memberStatus(isWorking: boolean, isReady: boolean, optionCount: number): MemberStatus {
  if (isWorking) return 'working';
  if (isReady) return 'ready';
  if (optionCount > 0) return 'needs-review';
  return 'not-ready';
}

function countLabel(count: number, singular: string): string {
  if (count === 0) return `No ${singular}s yet`;
  return `${count} ${singular}${count === 1 ? '' : 's'} ready`;
}

function TeamMemberCard({ member, disabled, onOpen }: {
  member: {
    key: StepKey;
    role: string;
    icon: string;
    brief: string;
    status: MemberStatus;
    metric: string;
    detail: string;
  };
  disabled: boolean;
  onOpen: () => void;
}) {
  const tone = MEMBER_TONE[member.status];
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onOpen}
      className={`group text-left rounded-[1.25rem] border p-4 min-h-[190px] transition-all disabled:cursor-not-allowed ${tone.card} ${disabled ? 'opacity-75' : 'hover:-translate-y-0.5 hover:border-accent-cyan/70'}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className={`material-symbols-outlined rounded-xl p-2 ${tone.icon}`}>{member.icon}</span>
          <div className="min-w-0">
            <p className="font-display text-lg font-bold leading-tight">{member.role}</p>
            <p className="mt-1 text-xs text-muted leading-relaxed">{member.brief}</p>
          </div>
        </div>
        <span className={`status-pill ${tone.pill}`}>{tone.label}</span>
      </div>
      <div className="mt-5">
        <p className="text-sm font-semibold text-text-primary line-clamp-1">{member.metric}</p>
        <p className="mt-2 text-xs text-muted leading-relaxed line-clamp-3">{member.detail}</p>
      </div>
      {!disabled && <p className="mt-4 text-xs text-accent-cyan opacity-0 group-hover:opacity-100 transition-opacity">Open room →</p>}
    </button>
  );
}

const MEMBER_TONE: Record<MemberStatus, { label: string; pill: string; card: string; icon: string }> = {
  'not-ready': {
    label: 'Waiting',
    pill: 'status-pill-muted',
    card: 'border-border-color bg-surface/70',
    icon: 'bg-surface-2 text-muted',
  },
  working: {
    label: 'Working',
    pill: 'status-pill-cyan',
    card: 'border-accent-cyan/50 bg-accent-cyan/10',
    icon: 'bg-accent-cyan/20 text-accent-cyan',
  },
  'needs-review': {
    label: 'Review',
    pill: 'status-pill-amber',
    card: 'border-accent-amber/50 bg-accent-amber/10',
    icon: 'bg-accent-amber/20 text-accent-amber',
  },
  ready: {
    label: 'Ready',
    pill: 'status-pill-emerald',
    card: 'border-border-color bg-surface/80',
    icon: 'bg-accent-emerald/15 text-accent-emerald',
  },
};

function RefreshConfirmDialog({
  counts,
  change,
  isRefreshing,
  onCancel,
  onConfirm,
  onReset,
}: {
  counts: { audiences: number; voices: number; personas: number; themes: number; samples: number } | undefined;
  change: { hasPrevious: boolean; briefChanged: boolean; rulesChanged: boolean; changeChars: number } | undefined;
  isRefreshing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  onReset: () => void;
}) {
  const anyChange = change ? (change.briefChanged || change.rulesChanged) : false;
  const noOpLikely = change ? change.hasPrevious && !anyChange : false;
  const items = counts
    ? [
        { label: 'audiences', n: counts.audiences },
        { label: 'voices', n: counts.voices },
        { label: 'personas', n: counts.personas },
        { label: 'themes', n: counts.themes },
      ].filter((i) => i.n > 0)
    : [];

  return (
    <div
      className="fixed inset-0 z-50 bg-background-dark/60 backdrop-blur-[2px] flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-surface border border-accent-cyan/40 rounded-xl shadow-2xl shadow-black/60 overflow-hidden"
      >
        <header className="px-6 py-5 border-b border-border-color">
          <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-accent-cyan mb-1">Refresh</p>
          <h2 className="font-display text-2xl font-bold tracking-tight text-text-primary">Refresh the strategy team?</h2>
          <p className="text-sm text-muted mt-2 leading-relaxed">
            Your team compares the current brief against the brief they were last trained on. Existing items get rewritten where the brief shifted; truly new angles get added. Names and your active/inactive curation are preserved.
          </p>
        </header>

        <div className="px-6 py-5 space-y-3">
          {noOpLikely && (
            <div className="rounded border border-accent-amber/40 bg-accent-amber/5 px-3 py-2.5">
              <p className="font-mono text-[10px] uppercase tracking-wider text-accent-amber mb-1">No change detected</p>
              <p className="text-xs text-text-primary leading-relaxed">
                Your brief and rules haven't changed since the team's last training. Refresh will be close to a no-op and still costs tokens. If you want a fresh team, hit{' '}
                <button type="button" onClick={onReset} className="text-accent-red hover:underline font-mono uppercase tracking-wider">Reset team</button>{' '}
                instead.
              </p>
            </div>
          )}
          {!noOpLikely && change?.hasPrevious && (
            <div className="rounded border border-accent-emerald/30 bg-accent-emerald/5 px-3 py-2.5">
              <p className="font-mono text-[10px] uppercase tracking-wider text-accent-emerald mb-1">Changes detected</p>
              <p className="text-xs text-text-primary leading-relaxed">
                {change.briefChanged && `Brief changed (~${change.changeChars} chars).`}
                {change.briefChanged && change.rulesChanged && ' '}
                {change.rulesChanged && 'Rules changed (banned phrases / voice rules / anti-conventions).'}
              </p>
            </div>
          )}
          {!change?.hasPrevious && (
            <div className="rounded border border-accent-cyan/30 bg-accent-cyan/5 px-3 py-2.5">
              <p className="font-mono text-[10px] uppercase tracking-wider text-accent-cyan mb-1">First refresh</p>
              <p className="text-xs text-text-primary leading-relaxed">
                No previous training is on file. Refresh will run a full update pass and establish the baseline for future Refreshes.
              </p>
            </div>
          )}

          {items.length > 0 && (
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted mb-2">Will review:</p>
              <ul className="space-y-1">
                {items.map((i) => (
                  <li key={i.label} className="flex items-center justify-between text-sm">
                    <span className="text-muted">{i.label}</span>
                    <span className="font-mono text-text-primary">{i.n}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border-color bg-surface-2/40">
          <button
            type="button"
            onClick={onCancel}
            disabled={isRefreshing}
            className="font-mono text-[11px] uppercase tracking-wider px-4 py-2 text-muted hover:text-text-primary transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isRefreshing}
            className="btn-bracket bg-primary text-white px-4 py-2 hover:bg-primary/90 disabled:opacity-40 transition-colors font-mono text-[11px] uppercase tracking-wider"
          >
            {isRefreshing ? 'Refreshing…' : 'Refresh team'}
          </button>
        </footer>
      </div>
    </div>
  );
}

function ResetConfirmDialog({
  counts,
  isResetting,
  onCancel,
  onConfirm,
}: {
  counts: { audiences: number; voices: number; personas: number; themes: number; samples: number } | undefined;
  isResetting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const items = counts
    ? [
        { label: 'audiences', n: counts.audiences },
        { label: 'voices', n: counts.voices },
        { label: 'personas', n: counts.personas },
        { label: 'themes', n: counts.themes },
        { label: 'samples', n: counts.samples },
      ].filter((i) => i.n > 0)
    : [];

  return (
    <div
      className="fixed inset-0 z-50 bg-background-dark/60 backdrop-blur-[2px] flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-surface border border-accent-red/40 rounded-xl shadow-2xl shadow-black/60 overflow-hidden"
      >
        <header className="px-6 py-5 border-b border-border-color">
          <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-accent-red mb-1">Destructive</p>
          <h2 className="font-display text-2xl font-bold tracking-tight text-text-primary">Reset the strategy team?</h2>
          <p className="text-sm text-muted mt-2 leading-relaxed">
            This wipes everything your strategy team has produced. Your brand kit and brief are preserved. Use this when the brand has changed materially.
          </p>
        </header>
        <div className="px-6 py-5 space-y-2">
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted mb-2">About to wipe:</p>
          {items.length === 0 ? (
            <p className="text-sm text-muted">Nothing to wipe. The strategy team hasn't produced anything yet.</p>
          ) : (
            <ul className="space-y-1">
              {items.map((i) => (
                <li key={i.label} className="flex items-center justify-between text-sm">
                  <span className="text-muted">{i.label}</span>
                  <span className="font-mono text-accent-red">{i.n}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <footer className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border-color bg-surface-2/40">
          <button
            type="button"
            onClick={onCancel}
            disabled={isResetting}
            className="font-mono text-[11px] uppercase tracking-wider px-4 py-2 text-muted hover:text-text-primary transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isResetting}
            className="btn-bracket bg-accent-red/20 text-accent-red border border-accent-red/50 px-4 py-2 hover:bg-accent-red/30 disabled:opacity-40 transition-colors font-mono text-[11px] uppercase tracking-wider"
          >
            {isResetting ? 'Resetting…' : 'Reset team'}
          </button>
        </footer>
      </div>
    </div>
  );
}
